import { FileActionButton } from "../file-action/file-action-controls";
import { Suspense, useEffect, useState } from "react";
import AppLoading from "../app-loading/app-loading";
import OilProductionSearchRecords from "./OilProductionRecords";
import OilProductionDataTable from "./OilProductionDataTable";
import { useTranslation } from "react-i18next";
import { OilIntakeLogModel } from "../../models/oilProduction-model";
import { BreadCrumb } from "primereact/breadcrumb";
import oilProductionService from "../../services/oilProduction/oilProductionservice";
import { oilProcessExport  } from "./oilProcessRecordList-Export";
import { exportOilIntakeLogs } from "../../services/oilProduction/oilProductionservice";

interface FilterParams {
  keywordSearch?: string | null;
  oilSeedID?: number | null;
  posID?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

interface SortParams {
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc" | null;
}

const OilProductionLog: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState<OilIntakeLogModel[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [currentFilters, setCurrentFilters] = useState<FilterParams>({});
  const [currentSort, setCurrentSort] = useState<SortParams>({});

  const handleSearch = (data: OilIntakeLogModel[], total: number, filters?: FilterParams) => {
    const sortedData = data ? [...data].sort((a, b) => b.id - a.id) : [];
    setFilteredData(sortedData);
    setTotalRecords(total);

    if (filters) {
      setCurrentFilters(filters);
    }
    setCurrentPage(1);
    setCurrentSort({});
  };

  const handlePageChange = (pageNumber: number, pageSize: number) => {
    setCurrentPage(pageNumber);
    setCurrentPageSize(pageSize);
    fetchOilIntakeLogs(pageNumber, pageSize, currentFilters, currentSort);
  };

  const handleSortChange = (sortColumn: string | null, sortDirection: "asc" | "desc" | null) => {
    const newSort = {
      sortColumn,
      sortDirection
    };
    setCurrentSort(newSort);
    setCurrentPage(1);
    fetchOilIntakeLogs(1, currentPageSize, currentFilters, newSort);
  };

  const fetchOilIntakeLogs = async (
    pageNumber: number = 1, 
    pageSize: number = 10, 
    filters: FilterParams = {},
    sortParams: SortParams = {}
  ) => {
    setLoading(true);
    try {
      const filter = {
        pageNumber,
        pageSize,
        ...filters,
        ...sortParams
      };
      const response = await oilProductionService.getAllOilIntakeLog(filter);
      const data = response.data || [];
      setFilteredData(data);
      setTotalRecords(response.totalRecords);
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setCurrentPage(1);
    setCurrentPageSize(10);
    setCurrentFilters({});
    setCurrentSort({});
    fetchOilIntakeLogs(1, 10, {}, {});
  };

  const handleDataRefresh = () => {
    fetchOilIntakeLogs(currentPage, currentPageSize, currentFilters, currentSort);
  };

  useEffect(() => {
    fetchOilIntakeLogs(1, 10);
  }, []);

  const handleExport = async () => {
    try {
      const exportFilter = {
        ...currentFilters,
        sortColumn: currentSort.sortColumn ?? null,
        sortDirection: currentSort.sortDirection ?? null,
      };

      const data = await exportOilIntakeLogs(exportFilter);

      if (!data || data.length === 0) {
        console.warn("⚠️ No records to export");
        return;
      }

      oilProcessExport(data);
    } catch (error) {
      console.error("Failed to export oil production logs", error);
    }
  };

  const breadcrumbItems = [
    { label: "Oil Production", url: "" },
    { label: "Oil Production Records", url: "" },
  ];
  const home = { icon: "pi pi-home", url: "/" };

  return (
    <Suspense fallback={<AppLoading />}>
      <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
        <div className="col-12">
          <div className="flex justify-content-between align-items-center">
            <div className="flex flex-column">
              <h5 className="ml-1 mb-2">{t("Oil Production Records")}</h5>
              {/* <BreadCrumb
                model={breadcrumbItems}
                home={home}
                className="bg-transparent px-0 py-0 text-sm text-gray-600 mb-3"
                style={{
                  border: "none",
                  boxShadow: "none",
                  background: "transparent",
                  marginLeft: "5px",
                }}
              /> */}
            </div>

            <FileActionButton action="export"
              className="text-black border-round mb-2"
              icon="pi pi-file-export"
              text
              raised
              tooltip="Export to Excel"
              tooltipOptions={{ position: "top" }}
              style={{ width: "auto" }}
              onClick={handleExport}
            >
              Export All Oil Production Records
            </FileActionButton>
          </div>

          <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
            <OilProductionSearchRecords 
              onSearch={handleSearch} 
              setLoading={setLoading} 
              onReset={handleReset}
            />
          </div>
        </div>

        <OilProductionDataTable 
          data={filteredData} 
          setLoading={setLoading} 
          totalRecords={totalRecords}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          pageNumber={currentPage}
          pageSize={currentPageSize}
          filters={currentFilters}
          sortParams={currentSort}
          onDataRefresh={handleDataRefresh} 
        />
      </div>

      {loading && <AppLoading />}
    </Suspense>
  );
};

export default OilProductionLog;
